import {
  canShowFlttRankingChart,
  formatClassementWeekDelta,
  formatPlaceWeekDelta,
  formatPointsValue,
  formatPointsWeekDelta,
  formatRankingPlace,
} from "../utils/flttRankingDisplay";
import { FlttRankingHistoryChart } from "./FlttRankingHistoryChart";
import "./FlttRankingSlip.css";

function figure(value) {
  return value == null || value === "" ? "—" : value;
}

function formatWeekLabel(weekStart) {
  if (!weekStart) return "—";
  const d = new Date(`${String(weekStart).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(weekStart).slice(0, 10);
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function moveTone(move) {
  return move?.tone || null;
}

function IconBadge({ children, tone }) {
  return (
    <span className={`fltt-slip__icon fltt-slip__icon--${tone || "brand"}`} aria-hidden="true">
      {children}
    </span>
  );
}

function IconClassement() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
      <path
        d="M7 4h10v3.2c0 2.4-1.7 4.4-4 4.9V14h2.5v2H8.5v-2H11v-1.9c-2.3-.5-4-2.5-4-4.9V4z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path d="M5 20h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IconPoints() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
      <path
        d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M18 15l.9 2.5L21.5 18l-2.6.9L18 21.5l-.9-2.6L14.5 18l2.6-.5L18 15z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconNational() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M7.5 20l1.2-4.2c.4-1.3 1.6-2.3 3-2.3h.6c1.4 0 2.6 1 3 2.3L16.5 20"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path d="M9 13.5l-3 1.2M15 13.5l3 1.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Official ranking bulletin: classement, rating points, national place,
 * this week's movement, and optional weekly history.
 */
export function FlttRankingSlip({
  ranking,
  history = [],
  t,
  showNotify = false,
  onNotify,
  notifying = false,
  showHistory = true,
  chartTone,
}) {
  if (!ranking) return null;
  const hasFigure =
    ranking.classement ||
    ranking.ranking_points != null ||
    ranking.ranking_place != null;
  if (!hasFigure) return null;

  const classMove = formatClassementWeekDelta(ranking);
  const pointsMove = formatPointsWeekDelta(ranking.points_delta);
  const placeMove = formatPlaceWeekDelta(ranking.place_delta);
  const noted = ranking.notice?.status === "notified";
  const points = formatPointsValue(ranking.ranking_points);
  const place = formatRankingPlace(ranking.ranking_place);
  const hasWeekMove = Boolean(pointsMove || placeMove || classMove);
  const historyRows = Array.isArray(history) ? [...history].reverse() : [];

  return (
    <section className="fltt-slip" aria-label={t("flttWeekChange", "This week")}>
      <div className="fltt-slip__head">
        <div className="fltt-slip__brand">
          <span className="fltt-slip__brand-mark" aria-hidden="true">
            <IconPoints />
          </span>
          <div className="fltt-slip__brand-text">
            <span className="fltt-slip__kicker">{t("flttVbRglPlace", "VB-RGL")}</span>
            <span className="fltt-slip__title">{t("ranking", "Ranking")}</span>
          </div>
        </div>
        {ranking.week_start ? (
          <span className="fltt-slip__week">
            {t("flttWeekUpdated", "Updated")} {formatWeekLabel(ranking.week_start)}
          </span>
        ) : null}
      </div>

      {hasWeekMove ? (
        <div className="fltt-slip__highlights" aria-label={t("flttWeekHighlight", "This week's update")}>
          {pointsMove ? (
            <div className={`fltt-slip__chip fltt-slip__chip--${pointsMove.tone}`}>
              <span className="fltt-slip__chip-label">{t("flttPointsWon", "Points this week")}</span>
              <strong>
                {pointsMove.text} {t("flttPtsShort", "pts")}
              </strong>
              {ranking.previous_ranking_points != null ? (
                <span className="fltt-slip__chip-prev">
                  {formatPointsValue(ranking.previous_ranking_points)} → {figure(points)}
                </span>
              ) : null}
            </div>
          ) : null}
          {placeMove ? (
            <div className={`fltt-slip__chip fltt-slip__chip--${placeMove.tone}`}>
              <span className="fltt-slip__chip-label">{t("flttNationalClimb", "National climb")}</span>
              <strong>{placeMove.text}</strong>
              {ranking.previous_ranking_place != null && place ? (
                <span className="fltt-slip__chip-prev">
                  {formatRankingPlace(ranking.previous_ranking_place)} → {place}
                </span>
              ) : null}
            </div>
          ) : null}
          {classMove ? (
            <div className={`fltt-slip__chip fltt-slip__chip--${classMove.tone}`}>
              <span className="fltt-slip__chip-label">{t("flttSlipClassement", "Classement")}</span>
              <strong>{classMove.text}</strong>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="fltt-slip__cols">
        <div
          className={[
            "fltt-slip__col",
            moveTone(classMove) ? `fltt-slip__col--${moveTone(classMove)}` : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <IconBadge tone="classement">
            <IconClassement />
          </IconBadge>
          <span className="fltt-slip__label">{t("flttSlipClassement", "Classement")}</span>
          <strong className="fltt-slip__figure">{figure(ranking.classement)}</strong>
          {classMove ? (
            <span className={`fltt-slip__move fltt-slip__move--${classMove.tone}`}>
              {classMove.text}
            </span>
          ) : (
            <span className="fltt-slip__move fltt-slip__move--quiet">—</span>
          )}
        </div>
        <div
          className={[
            "fltt-slip__col",
            moveTone(pointsMove) ? `fltt-slip__col--${moveTone(pointsMove)}` : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <IconBadge tone="points">
            <IconPoints />
          </IconBadge>
          <span className="fltt-slip__label">{t("flttSlipPoints", "Points")}</span>
          <strong className="fltt-slip__figure">{figure(points)}</strong>
          {pointsMove ? (
            <span className={`fltt-slip__move fltt-slip__move--${pointsMove.tone}`}>
              {pointsMove.text} {t("flttPtsShort", "pts")}
            </span>
          ) : (
            <span className="fltt-slip__move fltt-slip__move--quiet">—</span>
          )}
        </div>
        <div
          className={[
            "fltt-slip__col",
            moveTone(placeMove) ? `fltt-slip__col--${moveTone(placeMove)}` : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <IconBadge tone="national">
            <IconNational />
          </IconBadge>
          <span className="fltt-slip__label">{t("flttSlipNational", "National")}</span>
          <strong className="fltt-slip__figure">{figure(place)}</strong>
          {placeMove ? (
            <span className={`fltt-slip__move fltt-slip__move--${placeMove.tone}`}>
              {placeMove.text}
            </span>
          ) : (
            <span className="fltt-slip__move fltt-slip__move--quiet">—</span>
          )}
          {ranking.place_estimated && place ? (
            <span className="fltt-slip__estimate">{t("flttPlaceEstimated", "Estimated")}</span>
          ) : null}
        </div>
      </div>

      {noted ? (
        <span className="fltt-slip__stamp">{t("flttNotedByCoach", "Noted by coach")}</span>
      ) : null}
      {showNotify ? (
        <button
          type="button"
          className="fltt-slip__notify"
          disabled={notifying}
          onClick={onNotify}
        >
          {t("flttNotifyPlayer", "Notify player")}
        </button>
      ) : null}

      {showHistory && canShowFlttRankingChart(history) ? (
        <FlttRankingHistoryChart
          history={history}
          t={t}
          locale={t?.i18n?.language}
          tone={chartTone}
        />
      ) : null}

      {showHistory && historyRows.length > 1 ? (
        <div className="fltt-slip__history">
          <span className="fltt-slip__history-title">
            {t("flttRankingHistory", "Ranking history")}
          </span>
          <ol className="fltt-slip__history-list">
            {historyRows.map((row) => {
              const rowPoints = formatPointsWeekDelta(row.points_delta);
              const rowPlace = formatPlaceWeekDelta(row.place_delta);
              const key = row.week_start || `${row.ranking_points}-${row.ranking_place}`;
              return (
                <li key={key} className="fltt-slip__history-row">
                  <span className="fltt-slip__history-week">{formatWeekLabel(row.week_start)}</span>
                  <span className="fltt-slip__history-metric">
                    {row.classement || "—"}
                  </span>
                  <span className="fltt-slip__history-metric">
                    {formatPointsValue(row.ranking_points) ?? "—"}
                    {rowPoints ? (
                      <em className={`fltt-slip__history-delta fltt-slip__history-delta--${rowPoints.tone}`}>
                        {rowPoints.text}
                      </em>
                    ) : null}
                  </span>
                  <span className="fltt-slip__history-metric">
                    {formatRankingPlace(row.ranking_place) ?? "—"}
                    {rowPlace ? (
                      <em className={`fltt-slip__history-delta fltt-slip__history-delta--${rowPlace.tone}`}>
                        {rowPlace.text}
                      </em>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
